import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import { useCompareUrls } from '../shared/heicDisplay';
import {
    IconBolt,
    IconDownload,
    IconFileTypeJpg,
    IconFileTypePng,
    IconPhoto,
    IconRefresh,
    IconSparkles,
    IconWand,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import useImageFileEdits from '../../components/image/useImageFileEdits';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ImageGridPreview from '../shared/ImageGridPreview';
import ToolCard from '../../components/common/ToolCard';
import ImagePreviewModal from '../../../../../components/common/ImagePreviewModal';
import {
    ACCEPT,
    ENGINES,
    FORMAT_OPTIONS,
    MAX_FILE_BYTES,
    SCALE_OPTIONS,
    fitsOutputLimit,
    isSupportedUpscaleImageFile,
    readImageDimensions,
    upscaleImageFile,
} from './upscaleImageEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const ENGINE_OPTIONS = [
    { id: 'ai', labelKey: 'upscaleImage.engines.ai' },
    { id: 'aiMax', labelKey: 'upscaleImage.engines.aiMax' },
];

function segBtnClass(active) {
    return `flex flex-1 min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/**
 * Scale glyph: the same small source square every time, with the output frame
 * around it growing with the factor. Sizing the *source* by the factor (as this
 * first did) is the truthful mapping but reads backwards — 4× drew the smaller
 * block, and a glance says "bigger block, bigger number".
 */
function ScaleGlyph({ factor, active }) {
    const color = active ? '#F59E0B' : '#9A9AA3';
    const SOURCE = 8;
    const frame = factor === 2 ? 14 : 20;
    const bottom = 22;
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <rect
                x="2"
                y={bottom - frame}
                width={frame}
                height={frame}
                rx="2.5"
                fill="none"
                stroke={color}
                strokeWidth="1.6"
            />
            <rect x="2" y={bottom - SOURCE} width={SOURCE} height={SOURCE} rx="1.5" fill={color} opacity="0.9" />
        </svg>
    );
}

const ENGINE_ICONS = { ai: IconBolt, aiMax: IconSparkles };
const FORMAT_ICONS = { auto: IconWand, png: IconFileTypePng, jpeg: IconFileTypeJpg, webp: IconPhoto };

const fileKey = (file) => `${file.name}-${file.size}`;

export default function UpscaleImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [dims, setDims] = useState({});
    const [engine, setEngine] = useState('ai');
    const [scale, setScale] = useState(2);
    const [format, setFormat] = useState('original');
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);

    const invalidateResults = () => {
        setResults([]);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
    };

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const images = list.filter(isSupportedUpscaleImageFile);
        if (!images.length) {
            const msg = t('upscaleImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const file of images) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
                next.push(file);
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('upscaleImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
    }, [t]);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    // Dimensions drive the size preview and the output-limit check. Measuring
    // here (rather than on upload) also covers files that crop/rotate replaced.
    useEffect(() => {
        let cancelled = false;
        files.forEach((file) => {
            const key = fileKey(file);
            if (dims[key]) return;
            readImageDimensions(file)
                .then((d) => { if (!cancelled) setDims((prev) => ({ ...prev, [key]: d })); })
                .catch(() => { /* dimensions are a hint only */ });
        });
        return () => { cancelled = true; };
    }, [files, dims]);

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        invalidateResults();
    };

    // Crop / rotate / duplicate / delete, shared with every other image tool.
    // The edits rewrite the picked file, so the upscale pipeline is untouched.
    const { actions: fileActions, cropDialog } = useImageFileEdits({
        files,
        onReplace: (index, file) => {
            setFiles((prev) => prev.map((item, i) => (i === index ? file : item)));
            invalidateResults();
        },
        onInsert: (index, file) => {
            setFiles((prev) => {
                const next = [...prev];
                next.splice(index + 1, 0, file);
                return next;
            });
            invalidateResults();
        },
        onRemove: removeFile,
    });

    const handleReset = () => {
        setFiles([]);
        setDims({});
        invalidateResults();
    };

    // A file whose result would blow past the pixel ceiling at the chosen scale.
    const oversized = useMemo(
        () => files.filter((file) => {
            const d = dims[fileKey(file)];
            return d && !fitsOutputLimit(d.width, d.height, scale);
        }),
        [files, dims, scale],
    );

    // Files with no result yet — adding pictures to a finished batch queues only
    // the new ones, so nothing already upscaled is paid for twice.
    const pendingIndexes = useMemo(
        () => files.reduce((acc, _file, i) => (results[i] ? acc : [...acc, i]), []),
        [files, results],
    );
    const allDone = files.length > 0 && pendingIndexes.length === 0;
    const canRun = pendingIndexes.length > 0 && oversized.length === 0;

    const handleUpscale = async () => {
        if (!canRun || processing) return;

        recordToolUsage('image/upscale', { category: 'image' });

        setProcessing(true);
        setError(null);
        const queue = pendingIndexes;

        // The heavy model needs WebGPU, which iOS Safari and older browsers do
        // not have. Rather than dead-ending, the run drops to the light model and
        // says so — a demo on someone else's phone still produces a picture.
        let runEngine = engine;
        let fellBack = false;

        try {
            for (let k = 0; k < queue.length; k += 1) {
                const i = queue[k];
                const file = files[i];
                const onProgress = ({ stage, pct }) => {
                    setProgress({ fileIndex: k + 1, fileTotal: queue.length, stage, pct });
                };
                setProgress({ fileIndex: k + 1, fileTotal: queue.length, stage: 'decode', pct: 0 });

                let result;
                try {
                    result = await upscaleImageFile(file, { engine: runEngine, scale, format }, onProgress);
                } catch (err) {
                    if (err?.code !== 'NO_WEBGPU') throw err;
                    runEngine = 'ai';
                    fellBack = true;
                    result = await upscaleImageFile(file, { engine: runEngine, scale, format }, onProgress);
                }

                // Written at the file's own index (results line up with files) and
                // published immediately, so its card stops spinning right away.
                setResults((prev) => {
                    const next = [...prev];
                    next[i] = result;
                    return next;
                });
            }

            if (fellBack) setEngine('ai');
            notifications.show({
                title: t('common.success'),
                message: fellBack ? t('upscaleImage.fellBackToSimple') : t('upscaleImage.done'),
                color: fellBack ? 'yellow' : 'green',
            });
        } catch (err) {
            const msg = err?.code === 'TOO_LARGE'
                ? t('upscaleImage.errors.outputTooLarge')
                : t('upscaleImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const handleDownload = async () => {
        if (!results.length) return;

        if (results.length === 1) {
            downloadBlob(results[0].blob, results[0].name);
            return;
        }

        const zip = await zipImageFiles(results.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'upscaled-images.zip');
    };

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun,
        processing,
        hasResult: results.length > 0,
        hasError: !!error,
    });

    // ── Before/after lightbox ──
    // Object URLs live as long as the results do; the modal only ever gets URLs,
    // never blobs, so they are made once here instead of per render.
    const [lightboxIndex, setLightboxIndex] = useState(null);
    // HEIC originals are decoded for the "before" pane (see shared/heicDisplay).
    const compareUrls = useCompareUrls(results, files);

    const compareItems = useMemo(
        () => results
            .map((r, i) => ({
                id: i,
                fileName: r.name,
                // Keeps a baked-in edit in the same format the file already is.
                mimeType: r.blob.type || 'image/png',
                aiImage: compareUrls[i]?.after,
                // What the shared modal calls the "before" pane.
                uploadedImage: compareUrls[i]?.before,
            }))
            .filter((item) => item.aiImage),
        [results, compareUrls],
    );

    const openLightbox = (index) => {
        if (!results[index]) return;
        setPreviewIndex(index);
        setLightboxIndex(index);
    };

    const successLabel = results.length > 1
        ? `${t('upscaleImage.resultsTitle')} (${toBnDigits(results.length)})`
        : t('upscaleImage.done');

    // With several files the count is the useful line and stays put through
    // every stage; the model download is the one thing worth interrupting it for.
    const stageLabel = progress?.stage === 'download'
        ? t('upscaleImage.downloadingModel', { percent: toBnDigits(progress.pct || 0) })
        : progress?.fileTotal > 1
            ? t('upscaleImage.progressFile', {
                file: toBnDigits(progress.fileIndex),
                files: toBnDigits(progress.fileTotal),
            })
            : progress?.stage === 'encode'
                ? t('upscaleImage.encoding')
                : t('upscaleImage.upscaling');

    // The bar tracks the whole batch, not the file in hand: files already done
    // plus how far the current one has got. Otherwise it snapped back to zero on
    // every file while the label counted up. The one-time model download is left
    // out of the fraction — its own percentage is in the label.
    const overallProgress = useMemo(() => {
        if (!progress?.fileTotal) return progress?.pct;
        const within = progress.stage === 'download' ? 0 : (progress.pct || 0) / 100;
        const done = Math.max(0, progress.fileIndex - 1) + Math.min(1, within);
        return Math.round((done / progress.fileTotal) * 100);
    }, [progress]);

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('upscaleImage.done'),
    });

    return (
        <>
        <ToolPageLayout
            slug="image/upscale"
            subtitleKey="upscaleImage.subtitle"
            status={status}
            statusText={allDone && !processing ? successLabel : statusText}
            onReset={files.length || error ? handleReset : undefined}
            // Before a run the bar runs the tool; once every picture is done it downloads.
            primaryAction={allDone && !processing ? {
                label: results.length > 1 ? t('upscaleImage.downloadZip') : t('upscaleImage.download'),
                onClick: handleDownload,
                icon: IconDownload,
            } : {
                label: t('upscaleImage.upscale'),
                onClick: handleUpscale,
                disabled: !canRun || processing,
                loading: processing,
            }}
            // Drops the results only — the pictures and settings stay loaded, so another
            // run is one click away. Clearing everything is what রিসেট is for.
            secondaryAction={allDone && !processing ? {
                label: t('upscaleImage.startOver'),
                onClick: invalidateResults,
                icon: IconRefresh,
            } : undefined}
            showTrust={false}
            settings={(
                <div className="flex flex-col gap-3">
                    {/* Files are picked in the preview panel, so the left column
                        carries settings only — this input is what both the empty
                        state and the grid's "+" tile open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {oversized.length > 0 ? (
                        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] font-medium text-red-600 dark:border-red-500/30 dark:bg-red-950/30 dark:text-red-400">
                            {t('upscaleImage.errors.outputTooLarge')}
                        </p>
                    ) : null}

                    <ToolCard title={t('upscaleImage.scaleTitle')}>
                        <div className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {/* Glyphs only — the frame grows with the factor, and the
                                exact value is in the tooltip / aria-label. */}
                            {SCALE_OPTIONS.map((opt) => (
                                <button
                                    key={opt}
                                    type="button"
                                    onClick={() => { setScale(opt); invalidateResults(); }}
                                    disabled={processing}
                                    className={segBtnClass(scale === opt)}
                                    title={`${toBnDigits(opt)}x`}
                                    aria-label={`${toBnDigits(opt)}x`}
                                    aria-pressed={scale === opt}
                                >
                                    <ScaleGlyph factor={opt} active={scale === opt} />
                                </button>
                            ))}
                        </div>
                    </ToolCard>

                    <ToolCard title={t('upscaleImage.engineTitle')}>
                        <div className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {/* Icons only — the name is in the tooltip and read out
                                via aria-label. */}
                            {ENGINE_OPTIONS.map((opt) => {
                                const Icon = ENGINE_ICONS[opt.id];
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => { setEngine(opt.id); invalidateResults(); }}
                                        disabled={processing}
                                        className={segBtnClass(engine === opt.id)}
                                        title={t(opt.labelKey)}
                                        aria-label={t(opt.labelKey)}
                                        aria-pressed={engine === opt.id}
                                    >
                                        <Icon size={20} stroke={1.8} />
                                    </button>
                                );
                            })}
                        </div>
                    </ToolCard>

                    <OutputFormatPicker
                        value={format}
                        onChange={(next) => { setFormat(next); invalidateResults(); }}
                        formats={FORMAT_OPTIONS}
                        disabled={processing}
                        transparentOutput
                    />
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    // Fixed height suits one or two big pictures; a grid of three or more just hugs its rows.
                    tall={files.length <= 2}
                    title={t('upscaleImage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('upscaleImage.previewEmpty')}
                    idleHint={t('upscaleImage.previewEmptyHint')}
                    processingLabel={stageLabel}
                    progress={overallProgress}
                    error={error}
                    canvasTone="plain"
                    processingTone="none"
                    showIdle={!files.length && !error}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                >
                    {/* Kept mounted while processing: the frosted overlay above it
                        leaves the pictures visible, so the wait shows the work. */}
                    {(files.length > 0 || results.length > 0) && !error ? (
                        <ImageGridPreview
                            // One picture fills the panel, two sit side by side, three or more make a grid.
                            adaptive
                            actions={processing || results.length ? [] : fileActions}
                            files={files}
                            results={results}
                            variant="resize"
                            // Every card that has no result yet spins on its own, so
                            // a finished one can be opened while the rest run.
                            busyIndexes={processing ? pendingIndexes : undefined}
                            activeIndex={results.length ? previewIndex : undefined}
                            onItemClick={results.length ? openLightbox : undefined}
                            onAddMore={processing ? undefined : () => fileInputRef.current?.click()}
                            onAddDrop={processing ? undefined : handleDrop}
                            addLabel={t('upscaleImage.addMore')}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        {cropDialog}
        <ImagePreviewModal
            opened={lightboxIndex !== null}
            onClose={() => setLightboxIndex(null)}
            images={compareItems}
            initialImage={compareItems[lightboxIndex] ?? null}
            // `adjusted` is the upscaled image re-rendered with the viewer's
            // brightness/contrast/sharpness edits burnt in; without edits it is
            // null and the untouched result goes out.
            onDownloadImage={(img, adjusted) => {
                const r = results[img?.id];
                if (!r) return;
                downloadBlob(adjusted || r.blob, r.name);
            }}
            initialViewMode="side-by-side"
            useImageTitle
            hideEdit
            hidePrint
            hideShare
            hideCustomDownload
            hideDelete
        />
        </>
    );
}
